import { RowMeta, RowType } from "@shared/components";

/**
 * Optional deep-link decoration applied to a single grid row. Only the matched
 * document is highlighted; every other row is dimmed.
 */
export interface RowMetaDeepLink {
  isMatch: boolean;
  shouldDim: boolean;
  highlightId?: string;
}

export interface EntityDocumentRowMetaInput {
  /**
   * Whether the document should be rendered as a video row. Callers compute
   * this with their own discriminator (the selector path treats a `null`
   * videoId as a document, while the setup-folder path also guards against
   * `undefined`) so the shared shaping never changes their existing behavior.
   */
  isVideo: boolean;
  readOnlyFlag?: boolean | null;
  fileType?: string | null;
  isPendingOrApproved: boolean;
  deepLink?: RowMetaDeepLink;
}

/**
 * Single source of truth for the `rowMeta` shape of a DataRooms grid row.
 *
 * Both the Redux selectors (`entityDocumentsGridSelector` /
 * `makeEntityDocumentGridSelector`) and the client-side setup-folder builder
 * (`buildRootTile`) derive the same video / readonly / regular row types and
 * highlighted-vs-pending styling, so keeping that logic here prevents the two
 * paths from drifting apart.
 */
export function buildEntityDocumentRowMeta({
  isVideo,
  readOnlyFlag,
  fileType,
  isPendingOrApproved,
  deepLink,
}: EntityDocumentRowMetaInput): RowMeta {
  const deepLinkMeta: Pick<RowMeta, "highlightBorder" | "dimmed" | "highlightId"> = deepLink
    ? {
        highlightBorder: deepLink.isMatch,
        dimmed: deepLink.shouldDim,
        highlightId: deepLink.isMatch ? deepLink.highlightId : undefined,
      }
    : {};

  if (isVideo) {
    return {
      type: RowType.Video,
      ...deepLinkMeta,
      cellRenderers: {
        actionItem: () => null,
        download: () => null,
        markedRead: () => null,
      },
    };
  }

  if (readOnlyFlag) {
    return {
      nonClickableRow: !fileType,
      type: isPendingOrApproved ? RowType.HighlightedReadonly : RowType.Readonly,
      ...deepLinkMeta,
      cellRenderers: {
        download: () => null,
      },
    };
  }

  return {
    nonClickableRow: !fileType,
    type: isPendingOrApproved ? RowType.Highlighted : RowType.Regular,
    ...deepLinkMeta,
  };
}
